import React, { useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { LayoutDashboard, ClipboardList, UserCheck, Target, ListChecks, BarChart3, Gauge, Users, ShieldCheck, Palette, Database, Settings as SettingsIcon, Bell, Search, Menu, ChevronDown, ChevronRight } from 'lucide-react';
export function AppShell() {
  const location = useLocation();
  const navItems = [{
    path: '/app',
    label: 'Dashboard',
    icon: LayoutDashboard
  }, {
    path: '/app/assessments',
    label: 'Assessments',
    icon: ClipboardList
  }, {
    path: '/app/respondents',
    label: 'Respondents',
    icon: UserCheck
  }, {
    path: '/app/competencies',
    label: 'Competencies',
    icon: Target
  }, {
    path: '/app/questions',
    label: 'Questions',
    icon: ListChecks
  }, {
    path: '/app/reports',
    label: 'Reports & Insights',
    icon: BarChart3
  }, {
    path: '/app/scoring',
    label: 'Scoring Dashboard',
    icon: Gauge
  }];
  const settingsChildren = [{
    path: '/app/team',
    label: 'Directory',
    icon: Users
  }, {
    path: '/app/admin',
    label: 'Administration',
    icon: ShieldCheck
  }, {
    path: '/app/design-system',
    label: 'Design System',
    icon: Palette
  }, {
    path: '/app/schema',
    label: 'Database Schema',
    icon: Database
  }];
  const settingsPaths = ['/app/settings', ...settingsChildren.map(c => c.path)];
  const isSettingsSectionActive = settingsPaths.includes(location.pathname);
  // null = follow the active route; true/false = user has explicitly toggled
  const [settingsOpen, setSettingsOpen] = useState<boolean | null>(null);
  const isSettingsOpen = settingsOpen ?? isSettingsSectionActive;
  return <div className="flex h-screen bg-slate-50 text-slate-900 font-sans overflow-hidden" data-id="element-48" data-mp="koetxllesbd5">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-slate-200 flex flex-col hidden md:flex" data-id="element-49" data-mp="1acu0ma0bw4y7">
        <div className="h-16 flex items-center px-6 border-b border-slate-200" data-id="element-50" data-mp="1edw10u5zen1r">
          <div className="w-6 h-6 bg-slate-900 rounded-sm mr-3" data-id="element-51" data-mp="1efz0e4t0xsj5"></div>
          <span className="font-semibold text-lg tracking-tight" data-id="element-52" data-mp="1jbnuhsm9yh82">
            Oversight
          </span>
        </div>

        <div className="p-4" data-id="element-53" data-mp="1oa8ubqxm6yvz">
          <div className="relative mb-6" data-id="element-54" data-mp="1tboihrv39lil">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" data-id="element-55" data-mp="1tawub23tmq2c" />
            <input type="text" placeholder="Search..." className="w-full pl-9 pr-3 py-2 bg-slate-100 border-transparent rounded-md text-sm focus:bg-white focus:border-slate-300 focus:ring-2 focus:ring-slate-200 outline-none transition-all" data-id="element-56" data-mp="1xbylqwrhxxtr" />
          </div>

          <nav className="space-y-1" data-id="element-57" data-mp="2c37eheqp3zb4">
            {navItems.map(item => {
            const isActive = location.pathname === item.path;
            const Icon = item.icon;
            return <Link key={item.path} to={item.path} className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${isActive ? 'bg-slate-100 text-slate-900' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`} data-id="element-58" data-mp="5f8jtzpth6tp">
                  <Icon className={`mr-3 h-4 w-4 ${isActive ? 'text-slate-900' : 'text-slate-400'}`} data-id="element-59" data-mp="3mnviqkuw5xsu" />
                  {item.label}
                </Link>;
          })}

            {/* Settings group */}
            <div data-id="element-60" data-mp="edae7wf1p180">
              <div className={`flex items-center rounded-md transition-colors ${location.pathname === '/app/settings' ? 'bg-slate-100 text-slate-900' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`} data-id="element-61" data-mp="ecr7lbuuhz9a">
                <Link to="/app/settings" className="flex flex-1 items-center px-3 py-2 text-sm font-medium min-w-0" data-id="element-62" data-mp="123wnts4p6tk4">
                  <SettingsIcon className={`mr-3 h-4 w-4 shrink-0 ${location.pathname === '/app/settings' ? 'text-slate-900' : 'text-slate-400'}`} data-id="element-63" data-mp="mklohzhl11u9" />
                  Settings
                </Link>
                <button type="button" onClick={() => setSettingsOpen(!isSettingsOpen)} aria-expanded={isSettingsOpen} aria-controls="settings-subnav" aria-label={isSettingsOpen ? 'Collapse Settings section' : 'Expand Settings section'} className="mr-1 p-1.5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-200/60 transition-colors" data-id="element-64" data-mp="hmk1x4ky6ke1">
                  {isSettingsOpen ? <ChevronDown className="h-4 w-4" data-id="element-65" data-mp="1t1p8ijo7932x" /> : <ChevronRight className="h-4 w-4" data-id="element-66" data-mp="1t3tmjuftbigx" />}
                </button>
              </div>

              {isSettingsOpen && <div id="settings-subnav" className="mt-1 ml-4 pl-3 border-l border-slate-200 space-y-1" data-id="element-67" data-mp="38si0uh1dy8xd">
                  {settingsChildren.map(child => {
                const isActive = location.pathname === child.path;
                const Icon = child.icon;
                return <Link key={child.path} to={child.path} className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${isActive ? 'bg-slate-100 text-slate-900' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900'}`} data-id="element-68" data-mp="2puowrbf0zsye">
                        <Icon className={`mr-3 h-4 w-4 shrink-0 ${isActive ? 'text-slate-900' : 'text-slate-400'}`} data-id="element-69" data-mp="j2ec0gfit340" />
                        {child.label}
                      </Link>;
              })}
                </div>}
            </div>
          </nav>
        </div>

        <div className="mt-auto p-4 border-t border-slate-200" data-id="element-70" data-mp="3huik0u9wqoeg">
          <Link to="/app/profile" className="flex items-center hover:bg-slate-50 p-2 -mx-2 rounded-md transition-colors cursor-pointer" data-id="element-71" data-mp="3hsxmqww7krcw">
            <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-xs font-medium text-slate-600 shrink-0" data-id="element-72" data-mp="2p7xtfw19agby">
              JD
            </div>
            <div className="ml-3 overflow-hidden" data-id="element-73" data-mp="2y6swx0cx1cbk">
              <p className="text-sm font-medium text-slate-900 truncate" data-id="element-74" data-mp="2y584phe2q5gv">
                Jane Doe
              </p>
              <p className="text-xs text-slate-500 truncate" data-id="element-75" data-mp="26fvbmhcvou04">Program Manager</p>
            </div>
          </Link>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col h-screen overflow-hidden" data-id="element-76" data-mp="2afnmx1tnpe96">
        {/* Top Header */}
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-6" data-id="element-77" data-mp="22x6588bmmt54">
          <div className="flex items-center md:hidden" data-id="element-78" data-mp="1xwrv8kqgt94g">
            <Menu className="h-5 w-5 text-slate-500 mr-4" data-id="element-79" data-mp="1tw82rugu6ton" />
            <span className="font-semibold" data-id="element-80" data-mp="1oy6hr3zuokaj">Oversight</span>
          </div>
          <div className="hidden md:block" data-id="element-81" data-mp="1p0akvjpxdf9r">
            {/* Breadcrumbs could go here */}
          </div>
          <div className="flex items-center space-x-4" data-id="element-82" data-mp="1k169cmfj35g9">
            <button className="text-slate-400 hover:text-slate-600" data-id="element-83" data-mp="1f7btju31namg">
              <Bell className="h-5 w-5" data-id="element-84" data-mp="1a90k3kgnv24r" />
            </button>
            <Link to="/" className="text-xs font-medium text-slate-500 hover:text-slate-900 border border-slate-200 px-3 py-1.5 rounded-md" data-id="element-85" data-mp="1a77dg561shwr">
              Exit Prototype
            </Link>
          </div>
        </header>

        {/* Page Content */}
        <div className="flex-1 overflow-auto p-8" data-id="element-86" data-mp="waao685kv8hz">
          <div className="max-w-5xl mx-auto" data-id="element-87" data-mp="2k5p1pv3eo453">
            <Outlet data-id="element-88" data-mp="2k701zkxn6trg" />
          </div>
        </div>
      </main>
    </div>;
}